// Circular avatar picker: shows the current avatar (or initials fallback),
// with a small camera overlay button to upload/replace, and an "×" to
// remove. Handles resize + PATCH itself so callers just pass the user.
import { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { resizeImageToDataUrl } from "@/lib/imageResize";
import Avatar from "@/components/Avatar";

export default function AvatarUpload({ user, size = 96, onChange }) {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(user?.avatar_url || null);
  const currentUser = { ...user, avatar_url: preview };

  const pick = () => fileRef.current?.click();

  const patchAvatar = async (dataUrl) => {
    // /api/users/me is always allowed by the paywall middleware; we route
    // through /users/{id} for management-editing-somebody-else. Both call
    // the same PATCH endpoint.
    await api.patch(`/users/${user._id}`, { avatar_url: dataUrl });
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same file re-fires
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error(t("avatar_err_type") || "Alleen afbeeldingen zijn toegestaan");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error(t("avatar_err_size") || "Afbeelding is te groot (max 8 MB)");
      return;
    }
    try {
      setBusy(true);
      const dataUrl = await resizeImageToDataUrl(f, 320, 0.85);
      setPreview(dataUrl);
      await patchAvatar(dataUrl);
      onChange?.(dataUrl);
      toast.success(t("avatar_saved") || "Foto opgeslagen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error") || "Er ging iets mis");
      setPreview(user?.avatar_url || null);
    } finally {
      setBusy(false);
    }
  };

  const removeAvatar = async () => {
    try {
      setBusy(true);
      await patchAvatar("");
      setPreview(null);
      onChange?.(null);
      toast.success(t("avatar_removed") || "Foto verwijderd");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block" style={{ width: size, height: size }} data-testid="avatar-upload">
      <Avatar user={currentUser} size={size} fontSize={Math.round(size * 0.32)} />
      {/* Camera / upload button */}
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center shadow-lg hover:scale-110 active:scale-95 transition-transform ring-2 ring-[hsl(var(--surface))] disabled:opacity-70"
        title={preview ? (t("avatar_replace") || "Vervangen") : (t("avatar_upload") || "Upload foto")}
        data-testid="avatar-upload-btn"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} strokeWidth={2.2} />}
      </button>
      {/* Remove button (only when there IS a photo) */}
      {preview && !busy && (
        <button
          type="button"
          onClick={removeAvatar}
          className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[hsl(var(--surface))] text-[hsl(var(--danger))] border hairline grid place-items-center hover:bg-[hsl(var(--danger-soft))] transition-colors"
          title={t("avatar_remove") || "Verwijder foto"}
          data-testid="avatar-remove-btn"
        >
          <X size={12} strokeWidth={2.4} />
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        data-testid="avatar-file-input"
      />
    </div>
  );
}
